import { Link } from "react-router-dom";
import { Section, FinalCTA } from "@/components/v2/blocks";
import { Check, Search, BarChart3, BookOpen, MessageCircle, Scale, ReceiptText, Mail, ArrowRight } from "lucide-react";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import HowSteps from "@/components/v2/HowSteps";

const PLUS_LOIN = [
  { to: "/particuliers/comparer-les-offres", label: "Comparer les offres", sub: "Particuliers", icon: Scale },
  { to: "/particuliers/comprendre-sa-facture", label: "Comprendre sa facture", sub: "Particuliers", icon: ReceiptText },
  { to: "/contact", label: "Nous contacter", sub: "Étude gratuite", icon: Mail },
];

const IDEES = [
  ["Vais-je subir une coupure ?", "Non. Le changement de fournisseur n'entraîne généralement aucune interruption de l'alimentation en énergie."],
  ["Dois-je changer de compteur ?", "Non, dans la très grande majorité des cas."],
  ["Dois-je effectuer des travaux ?", "Non. Le changement de fournisseur ne nécessite aucun travaux dans le logement."],
  ["Les démarches sont-elles compliquées ?", "Non, elles sont généralement simples et encadrées."],
];

export default function ChangerFournisseur() {
  return (
    <div>
      <PageHeroPhoto
        cmsKey="changer"
        eyebrow="Particuliers · Changer de fournisseur"
        title="Changer de fournisseur d'énergie en toute sérénité"
        subtitle="De nombreux consommateurs pensent encore que changer de fournisseur est une démarche compliquée. En réalité, il s'agit d'une opération courante qui n'entraîne généralement aucune interruption de l'alimentation en énergie. Nous comparons et analysons les offres afin d'identifier le fournisseur d'énergie le mieux adapté à votre foyer."
        image="/img/changer-fournisseur.png"
        overlay="cinematic"
        flipLogo
        wideCard
        heroAside={
          <div className="flex justify-end -mr-2 lg:-mr-8">
            <div className="relative">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 360" role="img" aria-label="Point d'exclamation" className="relative h-56 w-auto md:h-72">
                {/* trait toujours visible + fond du « ! » */}
                <path
                  d="M94 28 C94 12, 146 12, 146 28 L136 260 L104 260 Z M120 301 A25 25 0 1 0 120 351 A25 25 0 1 0 120 301 Z"
                  fill="rgba(255,255,255,0.22)"
                  stroke="#5C7A2E"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* reflet lumineux : 1 passage sur la barre du ! ... */}
                <path
                  d="M94 28 C94 12, 146 12, 146 28 L136 260 L104 260 Z"
                  fill="none"
                  stroke="#C7FF2F"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="em-emark-comet"
                />
                {/* ... pour 2 passages dans le point */}
                <path
                  d="M120 301 A25 25 0 1 0 120 351 A25 25 0 1 0 120 301 Z"
                  fill="none"
                  stroke="#C7FF2F"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="em-qmark-comet--dot"
                />
              </svg>
            </div>
          </div>
        }
        highlightsLabel="Les idées reçues"
        highlights={[
          "Vais-je subir une coupure ? Non.",
          "Dois-je changer de compteur ? Non.",
          "Des travaux à prévoir ? Aucun.",
          "Des démarches compliquées ? Non.",
        ]}
      />

      <Section cmsKey="changer.idees" eyebrow="Les idées reçues" title="On vous dit tout" intro="Changer de fournisseur inquiète souvent à tort. Démêlons le vrai du faux.">
        <div className="grid gap-5 sm:grid-cols-2">
          {IDEES.map(([q, a], i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg">
              <span className="inline-block rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Idée reçue
              </span>
              <h3 className="mt-3 font-display text-base font-bold leading-snug text-muted-foreground/70 line-through decoration-muted-foreground/30 decoration-2">
                {q}
              </h3>
              <div className="mt-4 flex items-start gap-3 border-t border-border pt-4">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
                </span>
                <p className="text-sm font-medium leading-relaxed text-foreground">{a}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section cmsKey="changer.accompagnement" eyebrow="Notre accompagnement" title="Comment Energy Market peut vous aider" intro="Un accompagnement simple, de l'analyse de votre situation jusqu'à vos dernières questions." muted>
        <HowSteps
          icons={[Search, BarChart3, BookOpen, MessageCircle]}
          steps={[
            { title: "Analyse de la situation", text: "Nous étudions vos contrats et votre consommation." },
            { title: "Étude des solutions", text: "Nous comparons les offres adaptées à votre profil." },
            { title: "Accompagnement des démarches", text: "Nous vous expliquons chaque étape simplement." },
            { title: "Réponse à vos questions", text: "Nous restons disponibles pour vous éclairer." },
          ]}
        />
      </Section>

      <Section title="Pour aller plus loin">
        <div className="grid gap-4 sm:grid-cols-3">
          {PLUS_LOIN.map(({ to, label, sub, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:border-primary/40 hover:shadow-md"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" strokeWidth={2.1} aria-hidden />
              </span>
              <span className="flex-1">
                <span className="block text-sm font-semibold leading-snug text-primary">{label}</span>
                <span className="block text-xs text-muted-foreground">{sub}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
            </Link>
          ))}
        </div>
      </Section>

      <FinalCTA title="Faites analyser votre situation avant de décider" />
    </div>
  );
}
